// Battlefield gestures (2026-10-26, branch controls; the scheme and its sources: docs/CONTROLS.md). Pure: pointer
// events in, actions out, no DOM, so tests/controls_input.test.ts plays the rules through.
//
// Touch: a tap orders; a drag pans; two fingers pinch to zoom. Hold still for HOLD_MS and then drag to draw a selection
// box (War Legends' "tap and hold for a beat before dragging the little box", the one way reviewers praised; a plain
// drag stays the pan every praised port kept). Hold and let go without moving: a press (select that soldier's kind).
// With the Select button armed, the next drag draws a box straight away.
// Mouse: left drag draws a box at once, a left click is a tap, a right or middle drag pans, a right click is a tap with
// button 2 (the desktop order). A mouse never holds.

export type Mods = { shift: boolean; ctrl: boolean; alt: boolean };
export type Act =
  | ({ a: 'tap'; x: number; y: number; mouse: boolean; button: number; double: boolean } & Mods)
  /** A drag moved by dx, dy to x, y (CSS px): the camera keeps the ground that was under the finger under it. */
  | { a: 'pan'; dx: number; dy: number; x: number; y: number }
  /** Two fingers: the camera distance times k, and their midpoint moved by dx, dy to x, y (zoom and pan about it). */
  | { a: 'pinch'; k: number; x: number; y: number; dx: number; dy: number }
  /** Held still long enough: a box can start (the client shows it is armed). */
  | { a: 'hold'; x: number; y: number }
  | ({ a: 'box'; x0: number; y0: number; x1: number; y1: number; done: boolean } & Mods)
  /** Held and let go without moving. */
  | ({ a: 'press'; x: number; y: number } & Mods)
  /** A box or hold given up (a second finger landed). */
  | { a: 'cancel' };

/** How long a finger holds still before a drag draws a box (ms); how far it may wander and still be a tap or a hold
 *  (CSS px); two taps this close in time and space are a double. */
export const HOLD_MS = 320, SLOP = 10, DOUBLE_MS = 350, DOUBLE_PX = 28;

type Mode = 'wait' | 'held' | 'pan' | 'box' | 'pinch';
type Ptr = { x0: number; y0: number; x: number; y: number; t0: number; mouse: boolean; button: number; mods: Mods };

export class Gestures {
  /** The Select button: the next drag draws a box (it disarms after one). */
  armed = false;
  private pts = new Map<number, Ptr>();
  private mode: Mode = 'wait';
  private lead = -1;
  private pinch0 = 0;
  private mid0 = { x: 0, y: 0 };
  private lastTap = { t: -1e9, x: 0, y: 0 };

  constructor(private readonly out: (a: Act) => void) {}

  get busy() { return this.pts.size > 0; }

  down(id: number, x: number, y: number, t: number, mouse = false, button = 0, mods: Mods = NO_MODS) {
    this.pts.set(id, { x0: x, y0: y, x, y, t0: t, mouse, button, mods });
    if (this.pts.size === 1) { this.lead = id; this.mode = 'wait'; return; }
    // a second finger: pinch from here, whatever the first was doing
    if (this.mode === 'box' || this.mode === 'held') this.out({ a: 'cancel' });
    this.mode = 'pinch'; this.pinch0 = this.spread(); this.mid0 = this.mid();
  }

  move(id: number, x: number, y: number, t: number) {
    const p = this.pts.get(id);
    if (!p) return;
    const px = p.x, py = p.y;
    p.x = x; p.y = y;
    if (this.mode === 'pinch') {
      if (this.pts.size < 2) return;
      const d = this.spread(), m = this.mid();
      if (this.pinch0 > 0 && d > 0) this.out({ a: 'pinch', k: this.pinch0 / d, x: m.x, y: m.y, dx: m.x - this.mid0.x, dy: m.y - this.mid0.y });
      this.pinch0 = d; this.mid0 = m;
      return;
    }
    if (id !== this.lead) return;
    const far = Math.hypot(x - p.x0, y - p.y0) > SLOP;
    if (this.mode === 'wait') {
      this.tick(t);
      if (this.mode === 'wait') {
        if (!far) return;
        // a left mouse drag, or a drag with Select armed, draws a box; any other drag pans (from where it started)
        if ((p.mouse && p.button === 0) || (!p.mouse && this.armed)) { this.mode = 'box'; this.armed = false; }
        else { this.mode = 'pan'; this.out({ a: 'pan', dx: x - p.x0, dy: y - p.y0, x, y }); return; }
      }
    }
    if (this.mode === 'held') { if (!far) return; this.mode = 'box'; this.armed = false; } // one box per arming (Astra F5)
    if (this.mode === 'pan') this.out({ a: 'pan', dx: x - px, dy: y - py, x, y });
    else if (this.mode === 'box') this.out({ a: 'box', x0: p.x0, y0: p.y0, x1: x, y1: y, done: false, ...p.mods });
  }

  up(id: number, x: number, y: number, t: number) {
    const p = this.pts.get(id);
    if (!p) return;
    if (x !== p.x || y !== p.y) this.move(id, x, y, t); else this.tick(t); // a release where it was adds nothing but the time
    this.pts.delete(id);
    if (this.mode === 'pinch') { if (!this.pts.size) this.mode = 'wait'; this.rebase(); return; } // the fingers left over pan nothing
    if (id !== this.lead) return;
    this.lead = -1;
    if (this.mode === 'box') this.out({ a: 'box', x0: p.x0, y0: p.y0, x1: x, y1: y, done: true, ...p.mods });
    else if (this.mode === 'held') this.out({ a: 'press', x: p.x0, y: p.y0, ...p.mods });
    else if (this.mode === 'wait') {
      const L = this.lastTap, double = t - L.t < DOUBLE_MS && Math.hypot(x - L.x, y - L.y) < DOUBLE_PX;
      this.lastTap = double ? { t: -1e9, x, y } : { t, x, y };
      this.out({ a: 'tap', x: p.x0, y: p.y0, mouse: p.mouse, button: p.button, double, ...p.mods });
    }
    this.mode = 'wait';
  }

  /** The pointer was taken away (the browser cancelled it, or the window lost focus). */
  cancel(id: number) {
    if (!this.pts.delete(id)) return;
    if (this.mode === 'pinch') this.rebase();
    if (id === this.lead && (this.mode === 'box' || this.mode === 'held')) this.out({ a: 'cancel' });
    if (!this.pts.size) { this.mode = 'wait'; this.lead = -1; }
  }

  /** Call every frame: a finger held still for HOLD_MS arms a box. */
  tick(t: number) {
    const p = this.pts.get(this.lead);
    if (!p || this.mode !== 'wait' || p.mouse || this.pts.size !== 1) return;
    if (t - p.t0 >= HOLD_MS && Math.hypot(p.x - p.x0, p.y - p.y0) <= SLOP) { this.mode = 'held'; this.out({ a: 'hold', x: p.x0, y: p.y0 }); }
  }

  private spread() { const [a, b] = [...this.pts.values()]; return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0; }
  /** A finger left a pinch of three or more: measure the pair now tracked afresh, or its jump reads as a pinch and a pan
   *  (Astra F8, 2026-10-30). */
  private rebase() { this.pinch0 = this.spread(); this.mid0 = this.mid(); }
  private mid() { const [a, b] = [...this.pts.values()]; return a && b ? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } : { x: 0, y: 0 }; }
}

export const NO_MODS: Mods = { shift: false, ctrl: false, alt: false };
